/**
 * Behaviour for a rendered FAQ section template, ported from faqs.me's
 * engine/runtime.ts. faqs.me ships it as a string inside each export; here it
 * runs in the page that mounted the section, so it is plain functions.
 *
 * DOM conventions, all scoped to the section root:
 *   accordion  .fq-acc item > button.fq-qbtn[aria-expanded] + .fq-panel
 *              (grid-rows trick; `.fq-open` toggled on the item)
 *   tabs       button.fq-tab[data-cat] / .fq-pane[data-cat]
 *   search     input.fq-searchbox; rows carry [data-text]; .fq-empty shown when
 *              nothing matches; optional [data-defaults] wrapper keeps only
 *              [data-top] rows while the query is empty; .fq-reslabel swaps
 *              its [data-when] / [data-results] text
 *   carousel   .fq-spot panels; .fq-prev / .fq-next; .fq-count
 *   reveal     [data-rv] elements get .fq-in when they scroll into view
 */

function setUpAccordion(root, listen) {
    root.querySelectorAll('.fq-qbtn').forEach((button) => {
        listen(button, 'click', () => {
            const open = button.getAttribute('aria-expanded') === 'true';
            root.querySelectorAll('.fq-qbtn[aria-expanded="true"]').forEach((other) => {
                if (other !== button) {
                    other.setAttribute('aria-expanded', 'false');
                    other.closest('.fq-acc')?.classList.remove('fq-open');
                }
            });
            button.setAttribute('aria-expanded', String(!open));
            button.closest('.fq-acc')?.classList.toggle('fq-open', !open);
        });
    });
}

/**
 * Tabs follow the WAI-ARIA tabs pattern, which faqs.me's runtime stops short
 * of: one tab in the tab order at a time, arrow keys (and Home/End) move
 * between them, and each pane is a tabpanel labelled by its tab.
 */
function setUpTabs(root, listen) {
    const tabs = Array.from(root.querySelectorAll('.fq-tab'));
    if (!tabs.length) {
        return;
    }
    const panes = Array.from(root.querySelectorAll('.fq-pane'));
    const rootId = root.id || 'hcfq';

    tabs.forEach((tab, index) => {
        const cat = tab.getAttribute('data-cat');
        const tabId = `${rootId}-tab-${cat}`;
        tab.id = tabId;
        tab.setAttribute('tabindex', tab.getAttribute('aria-selected') === 'true' ? '0' : '-1');
        const pane = panes.find((candidate) => candidate.getAttribute('data-cat') === cat);
        if (pane) {
            pane.id = `${rootId}-pane-${cat}`;
            pane.setAttribute('role', 'tabpanel');
            pane.setAttribute('aria-labelledby', tabId);
            tab.setAttribute('aria-controls', pane.id);
        }

        listen(tab, 'click', () => select(tab));
        listen(tab, 'keydown', (event) => {
            const vertical = tab.parentElement?.getAttribute('aria-orientation') === 'vertical';
            const next = vertical ? 'ArrowDown' : 'ArrowRight';
            const previous = vertical ? 'ArrowUp' : 'ArrowLeft';
            let target = null;
            if (event.key === next) {
                target = tabs[(index + 1) % tabs.length];
            } else if (event.key === previous) {
                target = tabs[(index - 1 + tabs.length) % tabs.length];
            } else if (event.key === 'Home') {
                target = tabs[0];
            } else if (event.key === 'End') {
                target = tabs[tabs.length - 1];
            }
            if (target) {
                event.preventDefault();
                select(target);
                target.focus();
            }
        });
    });

    function select(selected) {
        const cat = selected.getAttribute('data-cat');
        tabs.forEach((tab) => {
            const on = tab === selected;
            tab.setAttribute('aria-selected', String(on));
            tab.setAttribute('tabindex', on ? '0' : '-1');
            tab.classList.toggle('fq-tab-on', on);
        });
        panes.forEach((pane) => {
            pane.hidden = pane.getAttribute('data-cat') !== cat;
        });
    }
}

function setUpSearch(root, listen) {
    const box = root.querySelector('.fq-searchbox');
    if (!box) {
        return;
    }
    listen(box, 'input', () => {
        const query = box.value.trim().toLowerCase();
        const keepsDefaults = !!root.querySelector('[data-defaults]');
        let any = false;
        root.querySelectorAll('[data-text]').forEach((row) => {
            const hit = query
                ? row.getAttribute('data-text').indexOf(query) !== -1
                : (keepsDefaults ? row.hasAttribute('data-top') : true);
            row.hidden = !hit;
            any = any || hit;
        });
        const empty = root.querySelector('.fq-empty');
        if (empty) {
            empty.hidden = any;
        }
        const label = root.querySelector('.fq-reslabel');
        if (label) {
            label.textContent = label.getAttribute(query ? 'data-results' : 'data-when') || label.textContent;
        }
    });
}

function setUpCarousel(root, listen) {
    const spots = Array.from(root.querySelectorAll('.fq-spot'));
    if (!spots.length) {
        return;
    }
    const count = root.querySelector('.fq-count');
    let current = 0;
    const show = (index) => {
        current = (index + spots.length) % spots.length;
        spots.forEach((spot, position) => {
            spot.hidden = position !== current;
        });
        if (count) {
            count.textContent = `${current + 1} / ${spots.length}`;
        }
    };
    const previous = root.querySelector('.fq-prev');
    const next = root.querySelector('.fq-next');
    if (previous) {
        listen(previous, 'click', () => show(current - 1));
    }
    if (next) {
        listen(next, 'click', () => show(current + 1));
    }
}

function setUpReveal(root, win, cleanups) {
    const targets = root.querySelectorAll('[data-rv]');
    const reduced = typeof win.matchMedia === 'function' && win.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!('IntersectionObserver' in win) || reduced) {
        targets.forEach((target) => target.classList.add('fq-in'));
        return;
    }
    const observer = new win.IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                entry.target.classList.add('fq-in');
                observer.unobserve(entry.target);
            }
        });
    }, { threshold: 0.12 });
    targets.forEach((target) => observer.observe(target));
    cleanups.push(() => observer.disconnect());
}

/**
 * Wire up a rendered section.
 *
 * @param {HTMLElement} root    the section root `renderFaqSection()` produced
 * @param {string[]} features   the template's `features`
 * @param {{ win?: Window }} [options]  `win` is the window the section lives
 *        in — the dashboard preview renders into an iframe, whose viewport is
 *        the one the reveal observer must watch
 * @returns {() => void} detaches every listener and observer
 */
export function attachFaqRuntime(root, features, options = {}) {
    const win = options.win || root.ownerDocument.defaultView || window;
    const cleanups = [];
    const listen = (target, type, handler) => {
        target.addEventListener(type, handler);
        cleanups.push(() => target.removeEventListener(type, handler));
    };

    if (features.includes('accordion')) {
        setUpAccordion(root, listen);
    }
    if (features.includes('tabs')) {
        setUpTabs(root, listen);
    }
    if (features.includes('search')) {
        setUpSearch(root, listen);
    }
    if (features.includes('carousel')) {
        setUpCarousel(root, listen);
    }
    if (features.includes('reveal')) {
        setUpReveal(root, win, cleanups);
    }

    return () => cleanups.splice(0).forEach((cleanup) => cleanup());
}
